import React, { useState, useEffect } from 'react';
import { stations, stationContacts } from '../services/api';

const MasterAHASS = ({ user }) => {
  const [stationList, setStationList] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (user.role === 'admin') {
      // Admin uses their own station
      setSelectedStation({ id: user.station_id, name: user.station_name });
      fetchContacts(user.station_id);
    } else {
      fetchStations();
    }
  }, []);

  const fetchStations = async () => {
    setLoading(true);
    try {
      const res = await stations.getAll();
      const stationsData = res.data.data;
      setStationList(stationsData);

      // Auto-select for admin
      if (user.role === 'admin' && user.station_id) {
        const myStation = stationsData.find(s => parseInt(s.id) === parseInt(user.station_id));
        if (myStation) {
          setSelectedStation(myStation);
          fetchContacts(myStation.id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch stations', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (stationId) => {
    setContactsLoading(true);
    try {
      const response = await stationContacts.getAll(stationId);
      setContacts(response.data.data || response.data || []);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleStationSelect = (station) => {
    setSelectedStation(station);
    fetchContacts(station.id);
  };

  const handleOpenModal = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({ name: contact.name, phone: contact.phone });
    } else {
      setEditingContact(null);
      setFormData({ name: '', phone: '' });
    }
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      if (editingContact) {
        await stationContacts.update(selectedStation.id, editingContact.id, formData);
      } else {
        await stationContacts.create(selectedStation.id, formData);
      }
      setShowModal(false);
      fetchContacts(selectedStation.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan data.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus nomor SA ini?')) return;
    setDeletingId(id);
    try {
      await stationContacts.delete(selectedStation.id, id);
      fetchContacts(selectedStation.id);
    } catch (err) {
      alert('Gagal menghapus data.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Master AHASS</h2>
          <p className="text-gray-500 text-sm mt-1">Kelola daftar cabang AHASS dan nomor WhatsApp Service Advisor.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Station List Sidebar - Only for Superadmin */}
        {user.role === 'superadmin' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-[600px]">
            <div className="p-4 border-b bg-gray-50 font-bold text-gray-800">Daftar AHASS</div>
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="p-4 text-center text-gray-400 text-sm">Memuat stations...</div>
              ) : (
                stationList.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleStationSelect(s)}
                    className={`w-full text-left p-3 rounded-lg mb-1 transition ${
                      selectedStation?.id === s.id ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <p className="font-bold text-sm truncate">{s.name}</p>
                    <p className={`text-xs ${selectedStation?.id === s.id ? 'text-blue-100' : 'text-gray-400'}`}>{s.code} - {s.city}</p>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Contacts Area */}
        <div className={`${user.role === 'superadmin' ? 'lg:col-span-2' : 'lg:col-span-3'} flex flex-col gap-6`}>
          {selectedStation ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col min-h-[600px]">
              <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                <div>
                  <h3 className="font-bold text-gray-800">Daftar SA: {selectedStation.name}</h3>
                  <p className="text-xs text-gray-500">{selectedStation.address}</p>
                </div>
                <button
                  onClick={() => handleOpenModal()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition"
                >
                  + Tambah SA
                </button>
              </div>

              <div className="flex-1 p-6">
                {contactsLoading ? (
                  <div className="text-center py-10 text-gray-400">Memuat daftar SA...</div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-10 flex flex-col items-center gap-3">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-400">Belum ada SA terdaftar untuk station ini.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b text-xs font-bold text-gray-400 uppercase tracking-wider">
                          <th className="pb-4 px-2">Nama Service Advisor</th>
                          <th className="pb-4 px-2">Nomor WhatsApp</th>
                          <th className="pb-4 px-2 text-right">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {contacts.map(c => (
                          <tr key={c.id} className="hover:bg-gray-50 transition">
                            <td className="py-4 px-2 font-bold text-gray-800">{c.name}</td>
                            <td className="py-4 px-2">
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold border border-green-200">
                                {c.phone}
                              </span>
                            </td>
                            <td className="py-4 px-2 text-right space-x-2">
                              <button
                                onClick={() => handleOpenModal(c)}
                                className="text-blue-600 hover:text-blue-800 p-2"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(c.id)}
                                disabled={deletingId === c.id}
                                className="text-red-600 hover:text-red-800 p-2 disabled:opacity-50"
                              >
                                {deletingId === c.id ? 'Menghapus...' : 'Hapus'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center p-20 text-center min-h-[600px]">
              <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-300 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-800">Pilih AHASS</h4>
              <p className="text-gray-400 text-sm max-w-xs mx-auto mt-2">
                Silahkan pilih cabang AHASS di samping untuk mengelola daftar nomor WhatsApp Service Advisor.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">{editingContact ? 'Edit Service Advisor' : 'Tambah Service Advisor Baru'}</h3>
              <p className="text-sm text-gray-500 mt-1">Isi data di bawah ini untuk mengelola notifikasi WhatsApp.</p>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama Lengkap SA</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Contoh: Budi Santoso"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nomor WhatsApp (Gunakan Format Internasional)</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Contoh: 628123456789"
                />
                <p className="text-[10px] text-gray-400 mt-1 font-medium">* Format diawali 62 (Tanpa tanda + dan Tanpa spasi)</p>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={isSaving}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-100 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    'Simpan Data'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterAHASS;
